import React, { useMemo, useState } from 'react';
import { useApp } from '../context/AppContext';
import { getVehicleBillTotal, parseDateOnlyLocal } from '../utils/helpers';
import MiniCalendar from './MiniCalendar';
import { isStageEnabled, isVehicleWorkTypeVisible, hasGarageLanesEnabled } from '../utils/enabledStages';

const formatCurrency = (value) => {
  const amount = Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2
  }).format(amount);
};

const Overview = ({ onOpenVehicleDetail, onNavigateToGarageVehicles, onNavigateToGarageReport, onNavigateToCalendar, onNavigateToFinance, onNavigateToSales, onOpenBooking, currentPage, recentVehicleIndices = [] }) => {
  const { vehicles, garageInfo, updateVehicle } = useApp();
  const [garageFinancialExpanded, setGarageFinancialExpanded] = useState(false);

  // Exclude archived vehicles from dashboard counts and financials
  const activeVehicles = useMemo(
    () => vehicles.filter((veh) => veh.status !== 'Archive' && !veh.deletedAt && !veh.collected),
    [vehicles]
  );

  const visibleVehicles = useMemo(
    () => activeVehicles.filter((veh) => isVehicleWorkTypeVisible(garageInfo, veh)),
    [activeVehicles, garageInfo]
  );

  // Calculate key stats (excluding archived)
  const stats = useMemo(() => {
    const total = visibleVehicles.length;
    const inProgressCount = visibleVehicles.filter((veh) =>
      veh.status === 'In Progress' || veh.status === 'Awaiting Parts'
    ).length;
    const salesCount = visibleVehicles.filter((veh) => veh.status === 'Sales').length;
    const workshopCount = visibleVehicles.filter((veh) => veh.status === 'Workshop').length;
    const motCount = visibleVehicles.filter((veh) => veh.status === 'MOT').length;
    const bodyshopCount = visibleVehicles.filter((veh) => veh.status === 'Bodyshop').length;
    const tyresCount = visibleVehicles.filter((veh) => veh.status === 'Tyres').length;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueTodayCount = visibleVehicles.filter((veh) => {
      if (!veh.collectionDate) return false;
      const date = parseDateOnlyLocal(veh.collectionDate);
      if (!date) return false;
      date.setHours(0, 0, 0, 0);
      return date.getTime() === today.getTime();
    }).length;

    // Garage only: vehicles with payment pending (exclude Sales)
    const paymentPendingCount = visibleVehicles.filter(
      (veh) => veh.status !== 'Sales' && !veh.paid
    ).length;

    // Calculate financial overview (excluding archived). vehicle.cost = subtotal; total = subtotal + VAT (MOT no VAT).
    const totalBilled = visibleVehicles.reduce((sum, veh) => sum + getVehicleBillTotal(veh), 0);
    const totalReceived = visibleVehicles
      .filter(veh => veh.paid)
      .reduce((sum, veh) => sum + getVehicleBillTotal(veh), 0);
    const totalOutstanding = visibleVehicles
      .filter(veh => !veh.paid)
      .reduce((sum, veh) => sum + getVehicleBillTotal(veh), 0);

    const dueTodayVehicles = visibleVehicles.filter((veh) => {
      if (!veh.collectionDate) return false;
      const d = parseDateOnlyLocal(veh.collectionDate);
      if (!d) return false;
      d.setHours(0, 0, 0, 0);
      return d.getTime() === today.getTime();
    });

    return {
      total,
      inProgressCount,
      salesCount,
      workshopCount,
      motCount,
      bodyshopCount,
      tyresCount,
      dueTodayCount,
      dueTodayVehicles,
      paymentPendingCount,
      totalBilled,
      totalReceived,
      totalOutstanding
    };
  }, [visibleVehicles]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  const getCompanyName = () => {
    // Use company name from garageInfo, or extract from address, or use default
    if (garageInfo?.companyName) {
      return garageInfo.companyName;
    }
    // Fallback: Extract company name from address (first part before comma)
    if (garageInfo?.address) {
      const companyName = garageInfo.address.split(',')[0].trim();
      if (companyName) {
        return companyName;
      }
    }
    // Final fallback to a generic name
    return 'Team';
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-left">
        <div className="dashboard-header" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
          <div>
            <p className="dashboard-kicker">Plutoo Dealer</p>
            <h1>{getGreeting()}, {getCompanyName()}</h1>
            <p className="dashboard-subtitle">Unite your workshop, sales, and operations — overview of what&apos;s happening today.</p>
          </div>
        </div>

        <div className="dashboard-stats-grid">
          {isStageEnabled(garageInfo, 'MOT') && (
          <div 
            className="dashboard-stat-card clickable" 
            onClick={() => onNavigateToGarageVehicles && onNavigateToGarageVehicles('MOT')}
            style={{ cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onNavigateToGarageVehicles && onNavigateToGarageVehicles('MOT');
              }
            }}
          >
            <div className="stat-icon stat-icon-secondary">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-label">MOT</div>
              <div className="stat-value">{stats.motCount}</div>
            </div>
          </div>
          )}

          {isStageEnabled(garageInfo, 'Workshop') && (
          <div 
            className="dashboard-stat-card clickable" 
            onClick={() => onNavigateToGarageVehicles && onNavigateToGarageVehicles('Workshop')}
            style={{ cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onNavigateToGarageVehicles && onNavigateToGarageVehicles('Workshop');
              }
            }}
          >
            <div className="stat-icon stat-icon-success">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-label">Workshop</div>
              <div className="stat-value">{stats.workshopCount}</div>
            </div>
          </div>
          )}

          {isStageEnabled(garageInfo, 'Bodyshop') && (
          <div 
            className="dashboard-stat-card clickable" 
            onClick={() => onNavigateToGarageVehicles && onNavigateToGarageVehicles('Bodyshop')}
            style={{ cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onNavigateToGarageVehicles && onNavigateToGarageVehicles('Bodyshop');
              }
            }}
          >
            <div className="stat-icon stat-icon-success">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="3" y1="9" x2="21" y2="9"></line>
                <line x1="9" y1="21" x2="9" y2="9"></line>
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-label">Bodyshop</div>
              <div className="stat-value">{stats.bodyshopCount}</div>
            </div>
          </div>
          )}

          {isStageEnabled(garageInfo, 'Tyres') && (
          <div 
            className="dashboard-stat-card clickable" 
            onClick={() => onNavigateToGarageVehicles && onNavigateToGarageVehicles('Tyres')}
            style={{ cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onNavigateToGarageVehicles && onNavigateToGarageVehicles('Tyres');
              }
            }}
          >
            <div className="stat-icon stat-icon-success">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="8"></circle>
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-label">Tyres</div>
              <div className="stat-value">{stats.tyresCount}</div>
            </div>
          </div>
          )}

          {hasGarageLanesEnabled(garageInfo) && (
          <div 
            className="dashboard-stat-card clickable" 
            onClick={() => onNavigateToGarageVehicles && onNavigateToGarageVehicles('In Progress')}
            style={{ cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onNavigateToGarageVehicles && onNavigateToGarageVehicles('In Progress');
              }
            }}
          >
            <div className="stat-icon stat-icon-warning">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-label">In Progress</div>
              <div className="stat-value">{stats.inProgressCount}</div>
            </div>
          </div>
          )}

          <div 
            className="dashboard-stat-card clickable" 
            onClick={() => onNavigateToCalendar && onNavigateToCalendar()}
            style={{ cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onNavigateToCalendar && onNavigateToCalendar();
              }
            }}
          >
            <div className="stat-icon stat-icon-info">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-label">Due Today</div>
              <div className="stat-value">{stats.dueTodayCount}</div>
            </div>
          </div>

          {hasGarageLanesEnabled(garageInfo) && (
          <div 
            className="dashboard-stat-card clickable" 
            onClick={() => onNavigateToGarageVehicles && onNavigateToGarageVehicles('Pending Payment')}
            style={{ cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onNavigateToGarageVehicles && onNavigateToGarageVehicles('Pending Payment');
              }
            }}
          >
            <div className="stat-icon stat-icon-warning">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-label">Pending Payment</div>
              <div className="stat-value">{stats.paymentPendingCount}</div>
            </div>
          </div>
          )}
        </div>

        <div className="dashboard-total-split" style={{ marginTop: '16px' }}>
          {(hasGarageLanesEnabled(garageInfo) || stats.total - stats.salesCount > 0) && (
          <div 
            className="dashboard-stat-card clickable dashboard-stat-total" 
            onClick={() => onNavigateToGarageVehicles && onNavigateToGarageVehicles('all')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onNavigateToGarageVehicles && onNavigateToGarageVehicles('all');
              }
            }}
          >
            <div className="stat-icon stat-icon-primary">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H8.5a1 1 0 0 0-.8.4L5 11l-2.36.86a1 1 0 0 0-.84.99V16h3" />
                <circle cx="7.5" cy="16.5" r="1.5" />
                <circle cx="16.5" cy="16.5" r="1.5" />
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-label">Garage</div>
              <div className="stat-value">{stats.total - stats.salesCount}</div>
            </div>
          </div>
          )}
          {isStageEnabled(garageInfo, 'Sales') && (
          <div 
            className="dashboard-stat-card clickable dashboard-stat-total" 
            onClick={() => onNavigateToSales && onNavigateToSales()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onNavigateToSales && onNavigateToSales();
              }
            }}
          >
            <div className="stat-icon stat-icon-danger">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-label">Sales</div>
              <div className="stat-value">{stats.salesCount}</div>
            </div>
          </div>
          )}
        </div>

        {recentVehicleIndices.length > 0 && (
          <div className="dashboard-financial-section" style={{ marginTop: '24px' }}>
            <h2 className="section-title">Recent jobs</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {recentVehicleIndices
                .filter((idx) => idx >= 0 && vehicles[idx] && !vehicles[idx].deletedAt && isVehicleWorkTypeVisible(garageInfo, vehicles[idx]))
                .slice(0, 5)
                .map((idx) => {
                  const veh = vehicles[idx];
                  if (!veh) return null;
                  return (
                    <button
                      key={`${veh.reg}-${idx}`}
                      type="button"
                      className="hero-button"
                      style={{ fontSize: '13px', padding: '8px 14px', background: 'var(--light-bg)', color: 'var(--text-dark)', border: '1px solid var(--border-color)' }}
                      onClick={() => onOpenVehicleDetail && onOpenVehicleDetail(idx)}
                    >
                      {veh.reg}
                      {veh.vehicle ? ` · ${veh.vehicle}` : ''}
                    </button>
                  );
                })}
            </div>
          </div>
        )}

        {stats.dueTodayVehicles && stats.dueTodayVehicles.length > 0 && (
          <div className="dashboard-financial-section" style={{ marginTop: '24px' }}>
            <h2 className="section-title">Collection due today</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {stats.dueTodayVehicles.map((veh) => {
                const vehicleIndex = vehicles.findIndex((v) => v.reg === veh.reg);
                return (
                  <div
                    key={veh.reg}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: '12px',
                      padding: '12px 16px',
                      background: 'var(--card-bg, #fff)',
                      border: '1px solid var(--border-color, #e5e7eb)',
                      borderRadius: '12px'
                    }}
                  >
                    <div
                      style={{ cursor: 'pointer', flex: '1 1 200px' }}
                      onClick={() => onOpenVehicleDetail && vehicleIndex >= 0 && onOpenVehicleDetail(vehicleIndex)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          if (onOpenVehicleDetail && vehicleIndex >= 0) onOpenVehicleDetail(vehicleIndex);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <strong>{veh.reg}</strong>
                      {veh.vehicle && <span style={{ marginLeft: '8px', color: 'var(--text-muted)' }}>{veh.vehicle}</span>}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="hero-button hero-primary"
                        style={{ fontSize: '13px', padding: '8px 14px' }}
                        onClick={() => {
                          if (vehicleIndex >= 0) updateVehicle(vehicleIndex, { collected: true });
                        }}
                      >
                        Mark collected
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="dashboard-financial-section">
          <button
            type="button"
            className="section-title section-title-toggle"
            onClick={() => setGarageFinancialExpanded((prev) => !prev)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              textAlign: 'left',
              font: 'inherit',
              color: 'inherit'
            }}
            aria-expanded={garageFinancialExpanded}
          >
            <span>Garage — Financial overview</span>
            <span style={{ opacity: 0.7, fontSize: '0.9em' }} aria-hidden>
              {garageFinancialExpanded ? '▼ Hide' : '▶ Show'}
            </span>
          </button>
          {garageFinancialExpanded && (
            <div className="financial-cards">
              <div 
                className="financial-card clickable" 
                onClick={() => onNavigateToGarageReport && onNavigateToGarageReport()}
                style={{ cursor: 'pointer' }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onNavigateToGarageReport && onNavigateToGarageReport();
                  }
                }}
              >
                <div className="financial-label">Total Billed</div>
                <div className="financial-value">{formatCurrency(stats.totalBilled)}</div>
                <div className="financial-subtitle">All garage invoices</div>
              </div>
              <div 
                className="financial-card clickable" 
                onClick={() => onNavigateToGarageVehicles && onNavigateToGarageVehicles('all')}
                style={{ cursor: 'pointer' }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onNavigateToGarageVehicles && onNavigateToGarageVehicles('all');
                  }
                }}
              >
                <div className="financial-label">Total Received</div>
                <div className="financial-value financial-received">{formatCurrency(stats.totalReceived)}</div>
                <div className="financial-subtitle">Paid vehicles</div>
              </div>
              <div 
                className="financial-card clickable" 
                onClick={() => onNavigateToGarageVehicles && onNavigateToGarageVehicles('Pending Payment')}
                style={{ cursor: 'pointer' }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onNavigateToGarageVehicles && onNavigateToGarageVehicles('Pending Payment');
                  }
                }}
              >
                <div className="financial-label">Outstanding</div>
                <div className="financial-value financial-outstanding">{formatCurrency(stats.totalOutstanding)}</div>
                <div className="financial-subtitle">Awaiting payment</div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="dashboard-right">
        <MiniCalendar onOpenVehicleDetail={onOpenVehicleDetail} currentPage={currentPage} />
      </div>
    </div>
  );
};

export default Overview;
